import styled, { CSSProperties } from 'styled-components'

import { getStateColor } from '@/presentation/utils'

export const TabContainer = styled.div<{ isPositionSticky?: boolean }>`
  width: 100%;
  position: ${({ isPositionSticky }) => (isPositionSticky ? 'sticky' : 'relative')};
  top: ${({ isPositionSticky }) => (isPositionSticky ? '0' : 'unset')};
  z-index: ${({ isPositionSticky }) => (isPositionSticky ? 2 : 'unset')};
  background-color: ${({ theme }) => theme.colors.secondaryD1};
`

interface TabScrollContainerProps {
  hasScrollButtons?: boolean
}

export const TabScrollContainer = styled.div<TabScrollContainerProps>`
  display: flex;
  justify-content: ${({ hasScrollButtons }) => (hasScrollButtons ? 'flex-start' : 'center')};
  align-items: center;
  gap: 8px;
  background-color: ${({ theme }) => theme.colors.secondaryD1};
  padding: 9px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  & {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
`

interface ScrollButtonProps {
  variant: 'left' | 'right'
}

export const ScrollButton = styled.button<ScrollButtonProps>`
  position: absolute;
  top: 50%;
  left: ${({ variant }) => (variant === 'left' ? 0 : 'unset')};
  right: ${({ variant }) => (variant === 'right' ? 0 : 'unset')};
  transform: translateY(-50%);
  z-index: 1;
  width: 32px;
  height: 48px;
  border: none;
  background-color: ${({ theme }) => `${theme.colors.secondaryD1}99`};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  transition: background-color 0.2s;
  &:hover {
    background-color: ${({ theme }) => getStateColor(`${theme.colors.secondaryD1}99`, 'hover')};
  }
  &:active {
    background-color: ${({ theme }) => getStateColor(`${theme.colors.secondaryD1}99`, 'pressed')};
  }
  &:disabled {
    cursor: not-allowed;
    background-color: ${({ theme }) => `${theme.colors.secondaryD1}66`};
    opacity: 0.5;
  }
`

interface TabProps {
  active: boolean
  size: 'sm' | 'md' | 'lg'
  textTransform?: CSSProperties['textTransform']
  error?: boolean
}

export const TabItem = styled.button<TabProps>`
  position: relative;
  flex: 1;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  border: 1px solid
    ${({ theme, active, error }) => (error ? theme.colors.error : active ? 'transparent' : theme.colors.secondaryL1)};
  color: ${({ theme, active }) => (active ? theme.colors.primaryL2 : theme.colors.neutralL5)};
  background-color: ${({ theme, active }) => (active ? `${theme.colors.primary}33` : theme.colors.secondaryD1)};

  border-radius: 50px;
  padding: ${({ size }) => {
    switch (size) {
      case 'sm':
        return '6px 12px'
      case 'md':
        return '8px 16px'
      case 'lg':
        return '14px 24px'
      default:
        return '10px 20px'
    }
  }};
  text-transform: ${({ textTransform }) => textTransform || 'uppercase'};
  white-space: nowrap;
  ${({ theme, size }) => {
    switch (size) {
      case 'sm':
        return theme.fonts.labelSM
      case 'md':
        return theme.fonts.titleXS
      case 'lg':
        return theme.fonts.titleMD
      default:
        return theme.fonts.titleXS
    }
  }}

  &:hover {
    background-color: ${({ theme, active }) =>
      active ? getStateColor(`${theme.colors.primaryD2}33`, 'hover') : theme.colors.secondaryD1};
  }

  &:active {
    background-color: ${({ theme, active }) =>
      active ? getStateColor(`${theme.colors.primaryD2}33`, 'pressed') : theme.colors.secondaryD1};
  }
`

export const TabBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  width: auto;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  text-transform: none;
  ${({ theme }) => theme.fonts.infoXS}
  color: ${({ theme }) => theme.colors.neutralL5};
  background-color: ${({ theme, active }) => (active ? theme.colors.primary : theme.colors.secondaryL2)};
`

export const TabSkeleton = styled.div<{ size: 'sm' | 'md' | 'lg' }>`
  flex: 1;
  height: ${({ size }) => {
    switch (size) {
      case 'sm':
        return '32px'
      case 'md':
        return '40px'
      case 'lg':
        return '56px'
      default:
        return '40px'
    }
  }};
  border-radius: 50px;
  background-color: ${({ theme }) => theme.colors.neutralL3};
  background-size: 200px 100%;
  background-repeat: no-repeat;
  background-image: linear-gradient(90deg, rgba(0, 0, 0, 0), rgb(255, 255, 255), rgba(0, 0, 0, 0));
  opacity: 0.1;
  animation: 1s ease-in-out 0s infinite normal none running skeleton-loading;
  @keyframes skeleton-loading {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
`
